import { LayerData, tLayerSettings } from '@/atoms';
import {
  NamedGeometry,
  parseAlignments,
  parseBreaklines,
  parseParcels,
  parsePipeNetworks,
  parseSurfaces,
} from '@/core/landxml';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

interface BatchedMeshData {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  id: string;
  layerId: string;
  origin: THREE.Vector3;
}

const MERGE_CHUNK_SIZE = 500;

export const LandXML = ({ layers }: { layers: LayerData[] }) => {
  const [meshes, setMeshes] = useState<BatchedMeshData[]>([]);
  const [lines, setLines] = useState<BatchedMeshData[]>([]);
  const layerSettings = useLayerSettingsMany(layers.map((l) => l.id));

  const surfaceMaterial = useCallback((layerSetting: tLayerSettings) => {
    // const defaultGray = new THREE.Color(0.7, 0.7, 0.7);
    // const color = defaultGray;
    const color = layerSetting.modelDefaultColor;

    return new THREE.MeshLambertMaterial({
      color,
      side: THREE.DoubleSide,
      wireframe: layerSetting.modelIsWireframe,
    });
  }, []);

  const lineMaterial = useMemo(() => new THREE.LineBasicMaterial({ color: 0x2196f3, linewidth: 1 }), []);

  const breaklineMaterial = useMemo(() => new THREE.LineBasicMaterial({ color: 0x787878, linewidth: 1 }), []);

  const pipeMaterial = useMemo(() => new THREE.MeshLambertMaterial({ color: 0x8b6914, side: THREE.DoubleSide }), []);

  const structMaterial = useMemo(() => new THREE.MeshLambertMaterial({ color: 0x607d8b, side: THREE.DoubleSide }), []);

  useEffect(() => {
    const abortController = new AbortController();
    setMeshes([]);
    setLines([]);

    const loadLandXML = async (file: string, layerId: string) => {
      try {
        const response = await fetch(`${import.meta.env.VITE_RESOURCES_ENDPOINT}/${file}`, {
          signal: abortController.signal,
          credentials: 'include',
        });
        const text = await response.text();
        if (abortController.signal.aborted) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'application/xml');
        console.log(doc);
        const parserError = doc.querySelector('parsererror');
        if (parserError) {
          console.error('LandXML parse error:', parserError.textContent);
          return;
        }

        const layerSetting = layerSettings[layerId];
        if (!layerSetting) return;

        // Parse all geometry types
        const surfaces = parseSurfaces(doc);
        const breaklines = parseBreaklines(doc);
        const alignments = parseAlignments(doc);
        const parcels = parseParcels(doc);
        const pipeNetworks = parsePipeNetworks(doc);

        // Compute origin from first available vertex
        let modelOrigin: THREE.Vector3 | null = null;
        for (const s of surfaces) {
          const pos = s.geometry.getAttribute('position');
          if (pos && pos.count > 0) {
            modelOrigin = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
            break;
          }
        }
        if (!modelOrigin) {
          for (const a of [...alignments, ...parcels, ...pipeNetworks.lines]) {
            const pos = a.geometry.getAttribute('position');
            if (pos && pos.count > 0) {
              modelOrigin = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
              break;
            }
          }
        }
        if (!modelOrigin && pipeNetworks.meshes.length > 0) {
          const pos = pipeNetworks.meshes[0].geometry.getAttribute('position');
          if (pos && pos.count > 0) {
            modelOrigin = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
          }
        }

        const finalOrigin = modelOrigin || new THREE.Vector3();
        if (abortController.signal.aborted) return;

        // Offset all geometries relative to origin
        const offsetGeometries = (geoms: NamedGeometry[]) => {
          for (const g of geoms) {
            g.geometry.translate(-finalOrigin.x, -finalOrigin.y, -finalOrigin.z);
          }
        };
        offsetGeometries(surfaces);
        offsetGeometries(breaklines);
        offsetGeometries(alignments);
        offsetGeometries(parcels);
        offsetGeometries(pipeNetworks.meshes);
        offsetGeometries(pipeNetworks.lines);

        // Batch surface meshes
        const newMeshes: BatchedMeshData[] = [];
        const surfaceGeometries = surfaces.map((s) => s.geometry);
        for (let i = 0; i < surfaceGeometries.length; i += MERGE_CHUNK_SIZE) {
          const chunk = surfaceGeometries.slice(i, i + MERGE_CHUNK_SIZE);
          const merged = mergeGeometries(chunk, false);
          if (merged) {
            merged.computeVertexNormals();
            newMeshes.push({
              geometry: merged,
              material: surfaceMaterial(layerSetting),
              id: `landxml-surface-${layerId}-${i}`,
              layerId,
              origin: finalOrigin.clone(),
            });
          }
          chunk.forEach((g) => g.dispose());
        }

        // Pipe network 3D meshes (pipes + structures)
        for (let i = 0; i < pipeNetworks.meshes.length; i++) {
          const geo = pipeNetworks.meshes[i].geometry;
          geo.computeVertexNormals();
          const hasCenterline = pipeNetworks.lines.some(
            (l) => l.name.replace(' (centerline)', '') === pipeNetworks.meshes[i].name,
          );
          newMeshes.push({
            geometry: geo,
            material: hasCenterline ? pipeMaterial : structMaterial,
            id: `landxml-pipe-mesh-${layerId}-${i}`,
            layerId,
            origin: finalOrigin.clone(),
          });
        }

        // Line geometries (alignments, parcels, pipe centerlines)
        const allLineGeoms = [...alignments, ...parcels, ...pipeNetworks.lines];
        const newLines: BatchedMeshData[] = allLineGeoms.map((l, i) => ({
          geometry: l.geometry,
          material: lineMaterial,
          id: `landxml-line-${layerId}-${i}`,
          layerId,
          origin: finalOrigin.clone(),
        }));

        // Breakline geometries
        const breaklineLines: BatchedMeshData[] = breaklines.map((l, i) => ({
          geometry: l.geometry,
          material: breaklineMaterial,
          id: `landxml-breakline-${layerId}-${i}`,
          layerId,
          origin: finalOrigin.clone(),
        }));
        newLines.push(...breaklineLines);

        if (!abortController.signal.aborted) {
          setMeshes((prev) => [...prev, ...newMeshes]);
          setLines((prev) => [...prev, ...newLines]);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error loading LandXML:', error);
        }
      }
    };

    layers.forEach((layer) => loadLandXML(layer.root!, layer.id));

    return () => abortController.abort();
  }, [layers, layerSettings, surfaceMaterial, lineMaterial, breaklineMaterial, pipeMaterial, structMaterial]);

  // Cleanup geometry on unmount
  useEffect(() => {
    return () => {
      meshes.forEach(({ geometry }) => geometry.dispose());
      lines.forEach(({ geometry }) => geometry.dispose());
    };
  }, [meshes, lines]);

  // Group meshes and lines by layerId to render each model with its own origin
  const meshesByLayer = useMemo(() => {
    const grouped = new Map<string, { meshes: BatchedMeshData[]; lines: BatchedMeshData[]; origin: THREE.Vector3 }>();
    for (const mesh of meshes) {
      if (!grouped.has(mesh.layerId)) {
        grouped.set(mesh.layerId, { meshes: [], lines: [], origin: mesh.origin });
      }
      grouped.get(mesh.layerId)!.meshes.push(mesh);
    }
    for (const line of lines) {
      if (!grouped.has(line.layerId)) {
        grouped.set(line.layerId, { meshes: [], lines: [], origin: line.origin });
      }
      grouped.get(line.layerId)!.lines.push(line);
    }
    return grouped;
  }, [meshes, lines]);

  return (
    <>
      {Array.from(meshesByLayer.entries())
        .filter(([layerId]) => !!layerSettings[layerId])
        .map(([layerId, { meshes: layerMeshes, lines: layerLines, origin }]) => (
          <group key={layerId} position={origin}>
            {layerMeshes.map(({ geometry, material, id }) => (
              <mesh key={id} geometry={geometry} material={material} />
            ))}
            {layerLines.map(({ geometry, material, id }) => (
              <line key={id} geometry={geometry} material={material} />
            ))}
          </group>
        ))}
    </>
  );
};
